/**
 * Movement, collision and ground resolution — deliberately pure.
 *
 * None of this imports Babylon. It takes numbers and returns numbers, which
 * means it can be tested properly without a GPU, and the same functions will be
 * reusable by the authoritative server in Stage 3 to check what a client claims
 * it did. That reuse is the reason it is separated at all.
 */
export const PLAYER_RADIUS = 0.45;
export const PLAYER_HEIGHT = 1.8;
export const WALK_SPEED = 3.4; // metres/second
export const RUN_SPEED = 6.6;
export const GRAVITY = -22; // metres/second², snappier than real gravity on purpose
export const JUMP_SPEED = 7.4;
/** Clamps a 2-D intent to the unit circle so diagonals are not faster. */
export function normaliseIntent(x, z) {
    const length = Math.hypot(x, z);
    if (length <= 1)
        return [x, z];
    return [x / length, z / length];
}
/**
 * Rotates a camera-space intent into world space.
 * `cameraYaw` is the direction the camera looks, in radians.
 */
export function intentToWorld(x, z, cameraYaw) {
    const sin = Math.sin(cameraYaw);
    const cos = Math.cos(cameraYaw);
    return [x * cos + z * sin, z * cos - x * sin];
}
/**
 * The inverse: turns a world-space direction back into a camera-space intent.
 *
 * Needed because click-to-move knows where the player should go in the world,
 * while everything downstream — the local prediction, the intent sent to the
 * server, the server's own simulation — speaks camera-space. Converting here
 * means a mouse click and a press of W arrive at `step()` as the same kind of
 * thing, so there is exactly one movement path to get right.
 */
export function worldToIntent(wx, wz, cameraYaw) {
    const sin = Math.sin(cameraYaw);
    const cos = Math.cos(cameraYaw);
    return [wx * cos - wz * sin, wx * sin + wz * cos];
}
/** Shortest signed angular difference, in (-π, π]. */
export function angleDelta(from, to) {
    let d = (to - from) % (Math.PI * 2);
    if (d > Math.PI)
        d -= Math.PI * 2;
    if (d <= -Math.PI)
        d += Math.PI * 2;
    return d;
}
/** Turns towards a target angle at a limited rate, so the character does not snap. */
export function turnTowards(current, target, maxStep) {
    const delta = angleDelta(current, target);
    if (Math.abs(delta) <= maxStep)
        return target;
    return current + Math.sign(delta) * maxStep;
}
/** World point -> a rectangle's local space. */
function toLocal(px, pz, f) {
    const dx = px - f.centre[0];
    const dz = pz - f.centre[1];
    if (f.rotation === 0)
        return [dx, dz];
    const sin = Math.sin(-f.rotation);
    const cos = Math.cos(-f.rotation);
    return [dx * cos - dz * sin, dx * sin + dz * cos];
}
function toWorldOffset(lx, lz, rotation) {
    if (rotation === 0)
        return [lx, lz];
    const sin = Math.sin(rotation);
    const cos = Math.cos(rotation);
    return [lx * cos - lz * sin, lx * sin + lz * cos];
}
/**
 * Pushes a circle out of a rotated rectangle, if they overlap.
 * Returns the corrected position, or null when there was no overlap.
 *
 * Handles the corner case literally: when the circle's centre is outside the
 * box on both axes, the nearest point is a corner, and pushing along a single
 * axis would shove the player through the adjacent wall.
 */
export function resolveCircleBox(px, pz, radius, f) {
    const [lx, lz] = toLocal(px, pz, f);
    const hx = f.half[0];
    const hz = f.half[1];
    const nearestX = Math.max(-hx, Math.min(hx, lx));
    const nearestZ = Math.max(-hz, Math.min(hz, lz));
    const dx = lx - nearestX;
    const dz = lz - nearestZ;
    const distanceSq = dx * dx + dz * dz;
    if (distanceSq > radius * radius)
        return null; // clear
    let outLx;
    let outLz;
    if (distanceSq > 1e-9) {
        // Outside the box, overlapping — push straight out along the nearest normal.
        const distance = Math.sqrt(distanceSq);
        outLx = nearestX + (dx / distance) * radius;
        outLz = nearestZ + (dz / distance) * radius;
    }
    else {
        // Centre is inside the box. Eject along whichever face is closest.
        const toRight = hx - lx;
        const toLeft = lx + hx;
        const toFront = hz - lz;
        const toBack = lz + hz;
        const smallest = Math.min(toRight, toLeft, toFront, toBack);
        outLx = lx;
        outLz = lz;
        if (smallest === toRight)
            outLx = hx + radius;
        else if (smallest === toLeft)
            outLx = -hx - radius;
        else if (smallest === toFront)
            outLz = hz + radius;
        else
            outLz = -hz - radius;
    }
    const [wx, wz] = toWorldOffset(outLx, outLz, f.rotation);
    return [f.centre[0] + wx, f.centre[1] + wz];
}
/**
 * Resolves a position against every footprint.
 *
 * Iterated a few times because pushing out of one building can push into
 * another — a single pass leaves the player standing inside a wall in the gap
 * between two close buildings.
 */
export function resolveCollisions(px, pz, radius, footprints, iterations = 3) {
    let x = px;
    let z = pz;
    for (let i = 0; i < iterations; i++) {
        let moved = false;
        for (const f of footprints) {
            const resolved = resolveCircleBox(x, z, radius, f);
            if (resolved !== null) {
                x = resolved[0];
                z = resolved[1];
                moved = true;
            }
        }
        if (!moved)
            break;
    }
    return [x, z];
}
/** Keeps the player inside the zone. */
export function clampToBounds(x, z, zone) {
    const r = PLAYER_RADIUS;
    return [
        Math.max(zone.bounds.min[0] + r, Math.min(zone.bounds.max[0] - r, x)),
        Math.max(zone.bounds.min[1] + r, Math.min(zone.bounds.max[1] - r, z))
    ];
}
/**
 * Ground height at a point, from the zone's terraces.
 *
 * Terraces are concentric rings. A point in a ring sits at that ring's height,
 * and the band where two rings meet is ramped so a player can walk up instead
 * of hitting an invisible step.
 */
export const TERRACE_RAMP = 3;
export function groundHeightAt(x, z, zone) {
    const terraces = zone.terrain.terraces;
    if (terraces === undefined || terraces.length === 0)
        return 0;
    // Sorted innermost first so each ring knows the height it is stepping up from.
    const rings = [...terraces].sort((a, b) => a.innerRadius - b.innerRadius);
    let previousHeight = 0;
    for (const ring of rings) {
        const distance = Math.hypot(x - ring.centre[0], z - ring.centre[1]);
        if (distance < ring.innerRadius)
            continue;
        if (distance <= ring.outerRadius) {
            // The ramp belongs at the ring's INNER edge, where the step up happens.
            // An earlier version ramped at the outer edge instead, which left a sheer
            // 2.4 m wall at every terrace boundary — walkable only by jumping.
            if (ring.innerRadius > 0 && distance < ring.innerRadius + TERRACE_RAMP) {
                const k = (distance - ring.innerRadius) / TERRACE_RAMP;
                return previousHeight + (ring.height - previousHeight) * k;
            }
            return ring.height;
        }
        previousHeight = ring.height;
    }
    // Past the outermost ring, ease back down to base ground so a raised area
    // (Saltmarch's inland rise) has a slope rather than a cliff at its edge.
    const last = rings[rings.length - 1];
    if (last === undefined)
        return 0;
    const distance = Math.hypot(x - last.centre[0], z - last.centre[1]);
    const beyond = distance - last.outerRadius;
    if (beyond >= TERRACE_RAMP)
        return 0;
    return last.height * (1 - beyond / TERRACE_RAMP);
}
/** One physics step. Pure: takes a state, returns the next state. */
export function step(state, intent, cameraYaw, zone, footprints, dt) {
    // A long frame (tab hidden, GC pause) must not teleport the player through a
    // wall, so the timestep is capped rather than trusted.
    const delta = Math.min(dt, 0.05);
    const [ix, iz] = normaliseIntent(intent.x, intent.z);
    const [wx, wz] = intentToWorld(ix, iz, cameraYaw);
    const magnitude = Math.hypot(wx, wz);
    const speed = intent.running ? RUN_SPEED : WALK_SPEED;
    let x = state.x + wx * speed * delta;
    let z = state.z + wz * speed * delta;
    [x, z] = resolveCollisions(x, z, PLAYER_RADIUS, footprints);
    [x, z] = clampToBounds(x, z, zone);
    // Face the direction of travel, but keep facing when standing still.
    let facing = state.facing;
    if (magnitude > 0.01) {
        facing = turnTowards(state.facing, Math.atan2(wx, wz), 12 * delta);
    }
    const ground = groundHeightAt(x, z, zone);
    let y = state.y;
    let velocityY = state.velocityY;
    let grounded = state.grounded;
    if (grounded && intent.jump) {
        velocityY = JUMP_SPEED;
        grounded = false;
    }
    velocityY += GRAVITY * delta;
    y += velocityY * delta;
    if (y <= ground) {
        y = ground;
        velocityY = 0;
        grounded = true;
    }
    else {
        grounded = false;
    }
    return { x, y, z, facing, velocityY, grounded };
}
/** True when the player is standing in an exit trigger. */
export function exitUnderPlayer(state, zone) {
    for (const exit of zone.exits) {
        const distance = Math.hypot(state.x - exit.position[0], state.z - exit.position[1]);
        if (distance <= exit.radius)
            return exit;
    }
    return null;
}
//# sourceMappingURL=movement.js.map